'use client';

import React from 'react';
import { CommonModal } from '@/components/common/modal';
import { DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ModalCloseButton } from '@/components/common/modal-close-button';
import MaterialIcons from '../../MaterialIcons/materialIcons';

interface LoginPromptModalProps {
  isOpen: boolean;
  type: 'skippable' | 'required';
  onClose: () => void;
  onLogin: () => void;
}

// Content configuration for each prompt type
const PROMPT_CONTENT = {
  skippable: {
    title: 'Keep learning without losing your place!',
    description:
      'Log in to keep track of your learning, resume courses anytime, and get personalized recommendations.',
  },
  required: {
    title: 'Login required to continue',
    description:
      'You need to log in to access this content and track your progress.',
  },
};

export function LoginPromptModal({
  isOpen,
  type,
  onClose,
  onLogin,
}: LoginPromptModalProps) {
  const content = PROMPT_CONTENT[type];
  const isSkippable = type === 'skippable';

  // For required modal, prevent closing via escape or clicking outside
  const handleOpenChange = (open: boolean) => {
    if (!open && isSkippable) {
      onClose();
    }
    // Required modal cannot be closed by clicking outside or pressing escape
  };

  return (
    <CommonModal
      open={isOpen}
      onOpenChange={handleOpenChange}
      showCloseButton={false}
      className="sm:max-w-[540px]"
    >
      <div className="login-modal-container">
        {isSkippable && <ModalCloseButton onClick={onClose} />}
        <div className="login-modal-content">
          <DialogTitle className="cs-h3 tab:h1-compact mb-2">
            {content.title}
          </DialogTitle>
          <p className="cs-s-body-light text-text-default-medium mb-7.5">
            {content.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center">
            {isSkippable && (
              <Button
                variant="SecondaryFillButton"
                onClick={onClose}
                className="cs-button w-full sm:w-auto sm:flex-1 touch-manipulation"
                aria-label="Close"
              >
                Close
              </Button>
            )}
            <Button
              variant="PrimaryFillButton"
              onClick={onLogin}
              className="cs-button w-full sm:w-auto sm:flex-1 touch-manipulation"
            >
              Log in
              <MaterialIcons iconName="arrow_right_alt" className="text-text-icon-subtle btn-icon !text-[18px] flex-shrink-0 leading-none transition-transform duration-300 ease-in-out group-hover:translate-x-1" />  
            </Button>
          </div>
        </div>
      </div>
    </CommonModal>
  );
}
